import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Mail, Instagram, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Invalid email";
    if (!form.message.trim()) e.message = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Danyale will reply soon.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Failed to send.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-2xl border-4 border-black bg-white font-body font-bold focus:outline-none focus:ring-4 focus:ring-pink-400 transition-all";
  const errCls = "border-red-600 focus:ring-red-300";

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-10 py-6 md:py-10" data-testid="contact-page">
      <Toaster position="top-center" richColors />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* Left: header + info */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          <div className="bg-black border-4 border-black rounded-[2rem] p-6 md:p-10 shadow-[10px_10px_0_0_rgba(0,0,0,1)]">
            <h1 className="font-display text-5xl md:text-7xl tracking-wide">
              <span className="text-pink-400">contact</span>
              <br />
              <span className="text-yellow-300">/me</span>
            </h1>
            <p className="mt-5 font-body font-bold text-yellow-200 text-base md:text-lg">
              Got a project, collab idea, or just want to say hi? Drop a note —
              I read every message.
            </p>
          </div>

          <div className="bg-[#FEFBEA] border-4 border-black rounded-2xl p-5 md:p-6 shadow-[6px_6px_0_0_rgba(0,0,0,1)] flex flex-col gap-3">
            <a
              href="mailto:dannykaysprod@gmail.com"
              data-testid="contact-email-link"
              className="flex items-center gap-3 font-body font-extrabold text-black hover:text-pink-600 transition-colors"
            >
              <span className="w-9 h-9 rounded-full border-2 border-black bg-pink-300 grid place-items-center">
                <Mail className="w-4 h-4" />
              </span>
              dannykaysprod@gmail.com
            </a>
            <a
              href="https://www.instagram.com/danykay.co?igsh=aHJma2FkNm1iYTZl"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram-link"
              className="flex items-center gap-3 font-body font-extrabold text-black hover:text-pink-600 transition-colors"
            >
              <span className="w-9 h-9 rounded-full border-2 border-black bg-yellow-300 grid place-items-center">
                <Instagram className="w-4 h-4" />
              </span>
              @danykay.co
            </a>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            className="bg-[#FEFBEA] border-4 border-black rounded-[2rem] p-6 md:p-10 shadow-[10px_10px_0_0_rgba(0,0,0,1)] flex flex-col gap-5"
            data-testid="contact-form"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="flex flex-col gap-2">
                <span className="font-body font-extrabold text-black uppercase text-xs">
                  Your name
                </span>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  data-testid="contact-input-name"
                  className={`${inputCls} ${errors.name ? errCls : ""}`}
                  placeholder="Jane Doe"
                />
                {errors.name && (
                  <span className="text-red-600 text-xs font-bold" data-testid="error-name">
                    {errors.name}
                  </span>
                )}
              </label>
              <label className="flex flex-col gap-2">
                <span className="font-body font-extrabold text-black uppercase text-xs">
                  Email
                </span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  data-testid="contact-input-email"
                  className={`${inputCls} ${errors.email ? errCls : ""}`}
                  placeholder="you@example.com"
                />
                {errors.email && (
                  <span className="text-red-600 text-xs font-bold" data-testid="error-email">
                    {errors.email}
                  </span>
                )}
              </label>
            </div>

            <label className="flex flex-col gap-2">
              <span className="font-body font-extrabold text-black uppercase text-xs">
                Subject (optional)
              </span>
              <input
                type="text"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                data-testid="contact-input-subject"
                className={inputCls}
                placeholder="Project inquiry"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="font-body font-extrabold text-black uppercase text-xs">
                Message
              </span>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid="contact-input-message"
                rows={6}
                className={`${inputCls} resize-none ${errors.message ? errCls : ""}`}
                placeholder="Tell me about your project…"
              />
              {errors.message && (
                <span className="text-red-600 text-xs font-bold" data-testid="error-message">
                  {errors.message}
                </span>
              )}
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-form-submit"
              className="self-start inline-flex items-center gap-3 px-7 py-4 rounded-full border-4 border-black bg-pink-600 text-white font-display text-2xl uppercase tracking-wide shadow-[6px_6px_0_0_rgba(0,0,0,1)] hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[2px_2px_0_0_rgba(0,0,0,1)] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> sending…
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" /> send message
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
